import type { BaseTableSettings, TableSettingsState } from '@cpce/shared';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/api/api.utils';
import { useSession } from '@/api/auth';
import { CommiteeIdResolutionStatus, useCommiteeIdResolutionFromUrl } from '@/utils/router';
import { tableSettingsQueryKeys } from './table-settings.query-keys';

export type TableSettingsRecord = Record<string, TableSettingsState>;

const ISO_DATE_START = /^\d{4}-\d{2}-\d{2}/;

function reviveRangeBound(bound: unknown): unknown {
  if (typeof bound !== 'string' || !ISO_DATE_START.test(bound)) return bound;
  const date = new Date(bound);
  return Number.isNaN(date.getTime()) ? bound : date;
}

/**
 * Filter values round-trip through JSON, which turns the Dates of date-range
 * filters into ISO strings; the DatePicker in the filter dropdown expects Dates back.
 */
function reviveFilterValue(value: unknown): unknown {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return value;
  if (!('min' in value) && !('max' in value)) return value;
  const range = value as { min?: unknown; max?: unknown };
  return { ...range, min: reviveRangeBound(range.min), max: reviveRangeBound(range.max) };
}

function reviveState(state: TableSettingsState): TableSettingsState {
  return {
    ...state,
    columnFilters: state.columnFilters.map((filter) => ({
      ...filter,
      value: reviveFilterValue(filter.value),
    })),
  };
}

function toRecord(rows: BaseTableSettings[]): TableSettingsRecord {
  return Object.fromEntries(rows.map((row) => [row.tableId, reviveState(row.state)]));
}

// The comité whose saved table states apply: the one in the URL, or none outside a comité.
// Undefined while the comité list is still loading, so callers can hold off.
export function useTableSettingsCommiteeId(): number | null | undefined {
  const resolution = useCommiteeIdResolutionFromUrl();
  if (resolution.status === CommiteeIdResolutionStatus.LOADING) return undefined;
  return resolution.status === CommiteeIdResolutionStatus.READY ? resolution.commiteeId : null;
}

// Raw useQuery instead of the query() util: fetching table settings must never toast —
// on failure the tables just fall back to their defaults.
export function useTableSettings() {
  const { data: session } = useSession();
  const commiteeId = useTableSettingsCommiteeId();
  const queryResult = useQuery({
    queryKey: tableSettingsQueryKeys.forCommitee(commiteeId ?? null),
    queryFn: () =>
      apiGet<BaseTableSettings[]>('/users/table-settings', {
        ...(commiteeId !== null && { commiteeId }),
      }),
    select: toRecord,
    enabled: !!session && commiteeId !== undefined,
    staleTime: 60_000,
  });
  return { data: queryResult.data, isLoading: queryResult.isPending };
}
